// Generated with util/create-component.js
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Input, Button, notification, Radio } from 'antd';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import { AdminUsersActionsContext, AdminUsersDataContext } from 'containers/data_context';

import { IAddUserFormProps } from './add_user_form.types';
import styles from './add_user_form.module.scss';

const initialValues = {
  email: '',
  name: '',
  password: '',
  confirmPassword: '',
  sendBookingNotificationEmail: true,
};

const validateMessages = {
  required: 'required',
};

const AddUserForm: React.FC<IAddUserFormProps> = ({ onCancel }) => {
  const { t } = useTranslation();

  const { createUser } = useContext(AdminUsersActionsContext);

  const {
    user: { isCreating },
  } = useContext(AdminUsersDataContext);

  const [form] = Form.useForm();

  const openNotificationWithIcon = (type: 'success' | 'error') => {
    if (type === 'success') {
      return notification['success']({
        message: t('general.success_message'),
      });
    } else {
      return notification['error']({
        message: t('general.error_message'),
        description: t('general.error_description'),
      });
    }
  };

  const handleSubmit = async (): Promise<void> => {
    const user = form.getFieldsValue(true);

    try {
      await createUser(user);
      openNotificationWithIcon('success');
      onCancel();
    } catch (e) {
      console.log(e, 'error');
      openNotificationWithIcon('error');
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const radioOptions = [
    { value: true, label: t('general.yes') },
    { value: false, label: t('general.no') },
  ];

  return (
    <div data-testid="AddUserForm" className={styles.root}>
      <Form
        form={form}
        initialValues={initialValues}
        onFinish={handleSubmit}
        validateMessages={validateMessages}
        layout="vertical"
      >
        <Form.Item
          label={t('general.other.email')}
          name="email"
          rules={[{ required: true }, { type: 'email', message: t('general.email.valid_message') }]}
        >
          <Input placeholder={t('general.other.email')} />
        </Form.Item>
        <Form.Item label={t('user.username')} name="name" rules={[{ required: true }]}>
          <Input placeholder={t('user.username')} />
        </Form.Item>

        <Form.Item
          label={t('general.other.password')}
          name="password"
          rules={[{ required: true }, { min: 6, message: t('general.password.validation_message') }]}
        >
          <Input.Password
            placeholder={t('general.other.password')}
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item
          label={t('general.password.confirm')}
          name="confirmPassword"
          data-testid="ConfirmPassword"
          rules={[
            {
              required: true,
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error(t('general.password.confirm_password.not_match')));
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={t('general.password.confirm')}
            iconRender={visible => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
          />
        </Form.Item>

        <Form.Item
          name="sendBookingNotificationEmail"
          label={t('user.send_booking_notification_email')}
          rules={[{ required: true }]}
        >
          <Radio.Group options={radioOptions} optionType="button" buttonStyle="solid" />
        </Form.Item>

        <div className={styles.footer}>
          <Form.Item>
            <Button type="default" onClick={handleCancel}>
              {t('link.cancel')}
            </Button>
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={isCreating}
              className={styles.save_button}
              disabled={isCreating}
            >
              {t('link.save_changes')}
            </Button>
          </Form.Item>
        </div>
      </Form>
    </div>
  );
};

export default AddUserForm;
